import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, HostListener, ViewChild, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { Calendar01Icon, Cancel01Icon, ArrowUp01Icon, ArrowDown01Icon, ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons';

export interface DateRange {
    start: string;
    end: string;
}

@Component({
    selector: 'app-date-picker',
    standalone: true,
    imports: [HugeiconsIconComponent],
    templateUrl: './date-picker.component.html',
    styleUrl: './date-picker.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true
        }
    ]
})
export class DatePickerComponent implements ControlValueAccessor, AfterViewChecked {
    @Input() placeholder: string = 'Select date';
    @Input() label: string = '';
    @Input() showLabel: boolean = true;
    @Input() maxDate: Date | null = null;

    /** When true, the picker selects a start + end date range */
    @Input() rangeMode: boolean = false;

    /**
     * Array of YYYY-MM-DD strings — these dates get a dot indicator in the
     * calendar so users can see at a glance which days have records.
     */
    @Input() set highlightDates(dates: string[]) {
        this._highlightSet = new Set(dates ?? []);
    }
    private _highlightSet: Set<string> = new Set();

    /** Emits a DateRange when both start and end are selected (range mode only) */
    @Output() rangeChange = new EventEmitter<DateRange | null>();

    // Icons
    calendarIcon   = Calendar01Icon;
    closeIcon      = Cancel01Icon;
    arrowUpIcon    = ArrowUp01Icon;
    arrowDownIcon  = ArrowDown01Icon;
    arrowLeftIcon  = ArrowLeft01Icon;
    arrowRightIcon = ArrowRight01Icon;

    @ViewChild('yearDropdownEl') yearDropdownEl?: ElementRef;

    isOpen = false;
    openUpward = false;
    monthDropdownOpen = false;
    yearDropdownOpen  = false;
    private shouldScrollYear = false;

    dropdownTop = 0;
    dropdownLeft = 0;

    private readonly DROPDOWN_HEIGHT = 420;

    selectedDate: Date | null = null;
    currentMonth: Date = new Date();

    // Range-mode state
    rangeStart: Date | null = null;
    rangeEnd:   Date | null = null;
    hoverDate:  Date | null = null;

    daysOfWeek  = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    calendarDays: (Date | null)[] = [];
    months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    quickRanges = [
        { label: 'Today',        fn: () => this.setQuickRange(0, 0) },
        { label: 'Yesterday',    fn: () => this.setQuickRange(-1, -1) },
        { label: 'Last 7 days',  fn: () => this.setQuickRange(-6, 0) },
        { label: 'Last 30 days', fn: () => this.setQuickRange(-29, 0) },
        { label: 'This month',   fn: () => this.setThisMonth() },
        { label: 'Last month',   fn: () => this.setLastMonth() },
    ];

    onChange:  any = () => { };
    onTouched: any = () => { };

    constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {
        this.generateCalendar();
    }

    // ── Display ──────────────────────────────────────────────────────────────

    get displayValue(): string {
        if (this.rangeMode) {
            if (this.rangeStart && this.rangeEnd) {
                return `${this.formatDisplay(this.rangeStart)} – ${this.formatDisplay(this.rangeEnd)}`;
            }
            if (this.rangeStart) return `${this.formatDisplay(this.rangeStart)} – …`;
            return '';
        }
        if (!this.selectedDate) return '';
        return this.formatDisplay(this.selectedDate);
    }

    get hasValue(): boolean {
        return this.rangeMode ? !!this.rangeStart : !!this.selectedDate;
    }

    private formatDisplay(date: Date): string {
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }

    get currentMonthLabel(): string {
        return this.currentMonth.toLocaleDateString('en-US', { month: 'long' });
    }

    get currentYearLabel(): number {
        return this.currentMonth.getFullYear();
    }

    get allYears(): number[] {
        const endYear = this.maxDate ? this.maxDate.getFullYear() : new Date().getFullYear() + 10;
        const years: number[] = [];
        for (let y = endYear; y >= 1924; y--) years.push(y);
        return years;
    }

    // ── Toggle ───────────────────────────────────────────────────────────────

    toggle() {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            this.monthDropdownOpen = false;
            this.yearDropdownOpen  = false;
            const anchor = this.rangeMode
                ? (this.rangeStart ?? new Date())
                : (this.selectedDate ?? new Date());
            this.currentMonth = new Date(anchor);
            this.generateCalendar();
            this.computeOpenDirection();
        }
    }

    private computeOpenDirection(): void {
        const host = this.elementRef.nativeElement as HTMLElement;
        const trigger = (host.querySelector('.datepicker-trigger') as HTMLElement) || host;
        const rect = trigger.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        this.openUpward = spaceBelow < this.DROPDOWN_HEIGHT && spaceAbove > spaceBelow;

        this.dropdownLeft = rect.left;
        if (this.openUpward) {
            const top = rect.top - this.DROPDOWN_HEIGHT - 8;
            this.dropdownTop = Math.max(8, top);
        } else {
            this.dropdownTop = rect.bottom + 8;
        }
        this.cdr.markForCheck();
    }

    @HostListener('window:resize')
    @HostListener('window:scroll')
    onWindowChange() {
        if (this.isOpen) this.computeOpenDirection();
    }

    // ── Month / Year dropdowns ────────────────────────────────────────────────

    toggleMonthDropdown(event: MouseEvent) {
        event.stopPropagation();
        this.monthDropdownOpen = !this.monthDropdownOpen;
        this.yearDropdownOpen  = false;
    }

    toggleYearDropdown(event: MouseEvent) {
        event.stopPropagation();
        this.yearDropdownOpen  = !this.yearDropdownOpen;
        this.monthDropdownOpen = false;
        if (this.yearDropdownOpen) this.shouldScrollYear = true;
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollYear && this.yearDropdownEl) {
            const el = this.yearDropdownEl.nativeElement as HTMLElement;
            const activeItem = el.querySelector('.header-dropdown-item.active') as HTMLElement;
            if (activeItem) {
                el.scrollTop = activeItem.offsetTop - el.offsetHeight / 2 + activeItem.offsetHeight / 2;
            }
            this.shouldScrollYear = false;
        }
    }

    selectMonthFromDropdown(monthIndex: number, event: MouseEvent) {
        event.stopPropagation();
        if (this.isMonthDisabled(monthIndex)) return;
        this.currentMonth = new Date(this.currentMonth.getFullYear(), monthIndex, 1);
        this.generateCalendar();
        this.monthDropdownOpen = false;
    }

    selectYearFromDropdown(year: number, event: MouseEvent) {
        event.stopPropagation();
        if (this.isYearDisabled(year)) return;
        this.currentMonth = new Date(year, this.currentMonth.getMonth(), 1);
        this.generateCalendar();
        this.yearDropdownOpen = false;
    }

    // ── Navigation ───────────────────────────────────────────────────────────

    prevMonth(event: MouseEvent) {
        event.stopPropagation();
        this.monthDropdownOpen = false;
        this.yearDropdownOpen  = false;
        this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
        this.generateCalendar();
    }

    nextMonth(event: MouseEvent) {
        event.stopPropagation();
        if (this.isNextMonthDisabled()) return;
        this.monthDropdownOpen = false;
        this.yearDropdownOpen  = false;
        this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
        this.generateCalendar();
    }

    // ── Day selection ─────────────────────────────────────────────────────────

    selectDate(date: Date) {
        if (this.isDateDisabled(date)) return;

        if (this.rangeMode) {
            this.selectRangeDate(date);
            return;
        }

        this.selectedDate = date;
        this.onChange(this.formatDate(date));
        this.isOpen = false;
    }

    private selectRangeDate(date: Date) {
        // If no start, or both already selected → start fresh
        if (!this.rangeStart || (this.rangeStart && this.rangeEnd)) {
            this.rangeStart = date;
            this.rangeEnd   = null;
            this.hoverDate  = null;
            return;
        }
        // Second click — ensure start <= end
        if (date < this.rangeStart) {
            this.rangeEnd   = this.rangeStart;
            this.rangeStart = date;
        } else {
            this.rangeEnd = date;
        }
        this.hoverDate = null;
        this.emitRange();
        this.isOpen = false;
    }

    onDayHover(date: Date | null) {
        if (!this.rangeMode || !this.rangeStart || this.rangeEnd) return;
        this.hoverDate = date;
    }

    private emitRange() {
        if (this.rangeStart && this.rangeEnd) {
            const range: DateRange = {
                start: this.formatDate(this.rangeStart),
                end:   this.formatDate(this.rangeEnd)
            };
            this.rangeChange.emit(range);
            this.onChange(range.start);
        }
    }

    clearDate(event: MouseEvent) {
        event.stopPropagation();
        if (this.rangeMode) {
            this.rangeStart = null;
            this.rangeEnd   = null;
            this.hoverDate  = null;
            this.rangeChange.emit(null);
            this.onChange(null);
        } else {
            this.selectedDate = null;
            this.onChange(null);
        }
    }

    // ── Quick ranges ──────────────────────────────────────────────────────────

    private setQuickRange(startOffset: number, endOffset: number) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const start = new Date(today);
        start.setDate(today.getDate() + startOffset);
        const end = new Date(today);
        end.setDate(today.getDate() + endOffset);
        this.rangeStart = start;
        this.rangeEnd   = end;
        this.hoverDate  = null;
        this.emitRange();
        this.isOpen = false;
    }

    private setThisMonth() {
        const now   = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        this.rangeStart = start;
        this.rangeEnd   = end;
        this.hoverDate  = null;
        this.emitRange();
        this.isOpen = false;
    }

    private setLastMonth() {
        const now   = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end   = new Date(now.getFullYear(), now.getMonth(), 0);
        this.rangeStart = start;
        this.rangeEnd   = end;
        this.hoverDate  = null;
        this.emitRange();
        this.isOpen = false;
    }

    // ── Range state helpers ───────────────────────────────────────────────────

    isRangeStart(date: Date | null): boolean {
        if (!date || !this.rangeStart) return false;
        return this.sameDay(date, this.rangeStart);
    }

    isRangeEnd(date: Date | null): boolean {
        if (!date) return false;
        const end = this.rangeEnd ?? (this.rangeStart && !this.rangeEnd ? this.hoverDate : null);
        if (!end) return false;
        return this.sameDay(date, end);
    }

    isInRange(date: Date | null): boolean {
        if (!date || !this.rangeStart) return false;
        const end = this.rangeEnd ?? this.hoverDate;
        if (!end) return false;
        const [s, e] = this.rangeStart <= end
            ? [this.rangeStart, end]
            : [end, this.rangeStart];
        return date > s && date < e;
    }

    private sameDay(a: Date, b: Date): boolean {
        return a.getFullYear() === b.getFullYear() &&
               a.getMonth()    === b.getMonth()    &&
               a.getDate()     === b.getDate();
    }

    // ── Disabled checks ───────────────────────────────────────────────────────

    isDateDisabled(date: Date | null): boolean {
        if (!date || !this.maxDate) return false;
        const max = new Date(this.maxDate);
        max.setHours(23, 59, 59, 999);
        return date > max;
    }

    isNextMonthDisabled(): boolean {
        if (!this.maxDate) return false;
        const nextMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
        const maxMonth  = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1);
        return nextMonth > maxMonth;
    }

    isMonthDisabled(monthIndex: number): boolean {
        if (!this.maxDate) return false;
        const year     = this.currentMonth.getFullYear();
        const maxYear  = this.maxDate.getFullYear();
        const maxMonth = this.maxDate.getMonth();
        if (year > maxYear) return true;
        if (year === maxYear && monthIndex > maxMonth) return true;
        return false;
    }

    isYearDisabled(year: number): boolean {
        if (!this.maxDate) return false;
        return year > this.maxDate.getFullYear();
    }

    // ── Calendar generation ───────────────────────────────────────────────────

    generateCalendar() {
        const year     = this.currentMonth.getFullYear();
        const month    = this.currentMonth.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const lastDate = new Date(year, month + 1, 0).getDate();

        this.calendarDays = [];
        for (let i = 0; i < firstDay; i++) this.calendarDays.push(null);
        for (let i = 1; i <= lastDate; i++) this.calendarDays.push(new Date(year, month, i));
    }

    isToday(date: Date | null): boolean {
        if (!date) return false;
        const today = new Date();
        return date.getDate() === today.getDate() &&
               date.getMonth() === today.getMonth() &&
               date.getFullYear() === today.getFullYear();
    }

    /** Returns true when this date has one or more records (shows a dot). */
    isHighlighted(date: Date | null): boolean {
        if (!date) return false;
        return this._highlightSet.has(this.formatDate(date));
    }

    isSelected(date: Date | null): boolean {
        if (!date || !this.selectedDate) return false;
        return date.getDate() === this.selectedDate.getDate() &&
               date.getMonth() === this.selectedDate.getMonth() &&
               date.getFullYear() === this.selectedDate.getFullYear();
    }

    formatDate(date: Date): string {
        const d    = new Date(date);
        let month  = '' + (d.getMonth() + 1);
        let day    = '' + d.getDate();
        const year = d.getFullYear();
        if (month.length < 2) month = '0' + month;
        if (day.length < 2)   day   = '0' + day;
        return [year, month, day].join('-');
    }

    /**
     * Parse a YYYY-MM-DD string as LOCAL time (avoids UTC-offset day-shift).
     * new Date('2026-02-22') → UTC midnight → Feb 21 in UTC-N timezones.
     * This instead builds new Date(year, month-1, day) which is always local.
     */
    private parseLocalDate(value: string): Date {
        const parts = value.split('-');
        if (parts.length === 3) {
            return new Date(+parts[0], +parts[1] - 1, +parts[2]);
        }
        return new Date(value); // fallback
    }

    // ── ControlValueAccessor ──────────────────────────────────────────────────

    writeValue(value: any): void {
        if (this.rangeMode) {
            if (value && typeof value === 'object' && value.start) {
                this.rangeStart   = this.parseLocalDate(value.start);
                this.rangeEnd     = value.end ? this.parseLocalDate(value.end) : null;
                this.currentMonth = new Date(this.rangeStart);
                this.generateCalendar();
            } else {
                this.rangeStart = null;
                this.rangeEnd   = null;
            }
        } else {
            if (value) {
                this.selectedDate = this.parseLocalDate(value);
                this.currentMonth = new Date(this.selectedDate);
                this.generateCalendar();
            } else {
                this.selectedDate = null;
            }
        }
    }

    registerOnChange(fn: any): void  { this.onChange  = fn; }
    registerOnTouched(fn: any): void { this.onTouched = fn; }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isOpen = false;
            this.monthDropdownOpen = false;
            this.yearDropdownOpen  = false;
        }
    }
}
